'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ComparisonFeature {
  feature: string;
  aiAgents: boolean;
  staffing: boolean;
  dataEngineering: boolean;
  licensing: boolean;
  copilot: boolean;
}

const ServiceComparison = () => {
  const [activeFilter, setActiveFilter] = useState<string>('all');

  const comparisonData: ComparisonFeature[] = [
    {
      feature: 'Custom Development',
      aiAgents: true,
      staffing: false,
      dataEngineering: true,
      licensing: false,
      copilot: true,
    },
    {
      feature: 'Dedicated Resources',
      aiAgents: true,
      staffing: true,
      dataEngineering: true,
      licensing: false,
      copilot: false,
    },
    {
      feature: 'Microsoft Ecosystem',
      aiAgents: true,
      staffing: false,
      dataEngineering: false,
      licensing: true,
      copilot: true,
    },
    {
      feature: 'Flexible Engagement',
      aiAgents: true,
      staffing: true,
      dataEngineering: true,
      licensing: true,
      copilot: false,
    },
    {
      feature: 'Training & Support',
      aiAgents: true,
      staffing: false,
      dataEngineering: true,
      licensing: true,
      copilot: true,
    },
    {
      feature: 'Scalable Solutions',
      aiAgents: true,
      staffing: true,
      dataEngineering: true,
      licensing: true,
      copilot: false,
    },
  ];

  const services = [
    { id: 'aiAgents', label: 'AI Agents', key: 'aiAgents' },
    { id: 'staffing', label: 'Staffing', key: 'staffing' },
    { id: 'dataEngineering', label: 'Data Engineering', key: 'dataEngineering' },
    { id: 'licensing', label: 'Licensing', key: 'licensing' },
    { id: 'copilot', label: 'Copilot', key: 'copilot' },
  ];

  const filteredServices =
    activeFilter === 'all'
      ? services
      : services.filter((s) => s.id === activeFilter);

  return (
    <section className="py-16 md:py-24 bg-muted">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
            Service Comparison Matrix
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Compare our services to find the perfect fit for your business needs
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-8">
          <button
            onClick={() => setActiveFilter('all')}
            className={`px-4 py-2 rounded-md font-headline font-semibold text-sm transition-all duration-base ${
              activeFilter === 'all' ?'bg-primary text-primary-foreground shadow-md' :'bg-card text-foreground hover:bg-muted-foreground hover:text-white'
            }`}
          >
            All Services
          </button>
          {services.map((service) => (
            <button
              key={service.id}
              onClick={() => setActiveFilter(service.id)}
              className={`px-4 py-2 rounded-md font-headline font-semibold text-sm transition-all duration-base ${
                activeFilter === service.id
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : 'bg-card text-foreground hover:bg-muted-foreground hover:text-white'
              }`}
            >
              {service.label}
            </button>
          ))}
        </div>

        <div className="bg-card rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-primary text-primary-foreground">
                  <th className="px-6 py-4 text-left font-headline font-bold text-sm md:text-base">
                    Feature
                  </th>
                  {filteredServices.map((service) => (
                    <th
                      key={service.id}
                      className="px-6 py-4 text-center font-headline font-bold text-sm md:text-base"
                    >
                      {service.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr
                    key={index}
                    className={`border-b border-border ${
                      index % 2 === 0 ? 'bg-background' : 'bg-muted'
                    }`}
                  >
                    <td className="px-6 py-4 font-body text-foreground font-semibold">
                      {row.feature}
                    </td>
                    {filteredServices.map((service) => (
                      <td key={service.id} className="px-6 py-4 text-center">
                        {row[service.key as keyof ComparisonFeature] ? (
                          <Icon
                            name="CheckCircleIcon"
                            size={24}
                            variant="solid"
                            className="text-success inline-block"
                          />
                        ) : (
                          <Icon
                            name="XCircleIcon"
                            size={24}
                            variant="solid"
                            className="text-muted-foreground inline-block opacity-30"
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceComparison;